import React, { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { financialEntryApi } from '../api';
import {
  FINANCIAL_VIEW_BY,
  ITEMS_PER_PAGE,
  getAccountLabel,
  getStatusLabel,
} from '../constants';
import { formatCalendarDateBR, formatDateTimeBR } from '../financialFormatters';
import {
  FinancialEntriesOutletContext,
  FinancialEntryListRow,
} from '../types';
import StatusBadge from './StatusBadge';
import ActionsDropdown from './ActionsDropdown';
import ViewFinancialEntryModal from '../ViewFinancialEntryModal';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage';
import { ErrorMessage, MessageContainer } from './style';

type FinancialRowKind = 'entry' | 'payment' | 'installment' | 'item';

interface FinancialTableRow {
  key: string;
  kind: FinancialRowKind;
  numero: string | number;
  entryNumber?: number | null;
  createdAt: string | null;
  value: number | null;
  company: string | null;
  businessUnit: string | null;
  status: number | null;
  type: number | null;
  dueDate: string | null;
  entry: FinancialEntryListRow;
  subRows?: FinancialTableRow[];
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** Keep dueDate key for table/export; source is competence month/year. */
const competenceToDueDate = (
  month: string | null | undefined,
  year: string | null | undefined,
): string | null => {
  const m = month?.trim().padStart(2, '0').slice(0, 2);
  const y = year?.trim().slice(0, 4);
  if (!m || !y || y.length !== 4) return null;
  return `${y}-${m}-01`;
};

const buildEntryRows = (
  rows: FinancialEntryListRow[],
  viewBy: string,
): FinancialTableRow[] =>
  rows.map((row) => {
    const subRows: FinancialTableRow[] =
      viewBy === FINANCIAL_VIEW_BY.ITEM
        ? (row.items ?? []).map((item) => ({
            key: `${viewBy}-${row.id}-item-${item.id}`,
            kind: 'item' as const,
            numero: item.id,
            createdAt: null,
            value: null,
            company: row.company,
            businessUnit: item.businessUnit,
            status: row.status,
            type: row.type,
            dueDate: competenceToDueDate(item.competenceMonth, item.competenceYear),
            entry: row,
          }))
        : (row.installments ?? []).map((installment) => ({
            key: `${viewBy}-${row.id}-inst-${installment.id}`,
            kind: 'installment' as const,
            numero: installment.installmentNumber?.trim() || '—',
            createdAt: null,
            value: installment.value,
            company: row.company,
            businessUnit: row.businessUnit,
            status: row.status,
            type: row.type,
            dueDate: installment.dueDate,
            entry: row,
          }));

    return {
      key: `${viewBy}-${row.id}`,
      kind: 'entry' as const,
      numero: row.id,
      createdAt: row.createdAt ?? null,
      value: row.totalValue,
      company: row.company,
      businessUnit: row.businessUnit,
      status: row.status,
      type: row.type,
      dueDate: row.dueDate,
      entry: row,
      subRows,
    };
  });

const buildPaymentRows = (rows: FinancialEntryListRow[]): FinancialTableRow[] =>
  rows.map((row) => ({
    key: `payment-${row.id}`,
    kind: 'payment' as const,
    numero: row.id,
    entryNumber: row.entryId ?? null,
    createdAt: row.createdAt ?? null,
    value: row.value ?? row.totalValue,
    company: row.company,
    businessUnit: row.businessUnit,
    status: row.status,
    type: row.type,
    dueDate: row.dueDate,
    entry: row,
  }));

const FinancialEntriesTable: React.FC = () => {
  const {
    search,
    filters,
    refreshKey,
    triggerRefresh,
  } = useOutletContext<FinancialEntriesOutletContext>();

  const [viewEntryId, setViewEntryId] = useState<number | null>(null);

  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const isPaymentView = viewBy === FINANCIAL_VIEW_BY.PAYMENT;
  const isItemView = viewBy === FINANCIAL_VIEW_BY.ITEM;

  const queryFilters = useMemo(
    () => ({
      ...filters,
      search: search || undefined,
      limit: ITEMS_PER_PAGE,
      viewBy,
    }),
    [filters, search, viewBy],
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['financialEntries', queryFilters, refreshKey],
    queryFn: ({ pageParam = 1 }) =>
      financialEntryApi.list({ ...queryFilters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const fetched = lastPage.page * lastPage.limit;
      if (fetched < lastPage.total) return lastPage.page + 1;
      return undefined;
    },
  });

  const rawRows: FinancialEntryListRow[] = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const tableRows = useMemo(
    () => (isPaymentView ? buildPaymentRows(rawRows) : buildEntryRows(rawRows, viewBy)),
    [rawRows, isPaymentView, viewBy],
  );

  const total = data?.pages[0]?.total ?? 0;

  const handleEntryUpdated = () => {
    triggerRefresh();
    refetch();
  };

  const columns = useMemo<StandardColumn<FinancialTableRow>[]>(() => {
    const sharedColumns: StandardColumn<FinancialTableRow>[] = [
      {
        id: 'numero',
        label: 'Número',
        accessor: (row) => row.numero,
      },
      {
        id: 'createdAt',
        label: 'Registro',
        accessor: (row) => row.createdAt ?? '',
        cell: (row) => <>{row.createdAt ? formatDateTimeBR(row.createdAt) : '—'}</>,
        exportValue: (row) => (row.createdAt ? formatDateTimeBR(row.createdAt) : '—'),
      },
      ...(!isItemView
        ? [
            {
              id: 'value',
              label: 'Valor',
              accessor: (row: FinancialTableRow) => row.value ?? 0,
              cell: (row: FinancialTableRow) => <>{formatCurrency(row.value)}</>,
              exportValue: (row: FinancialTableRow) => formatCurrency(row.value),
            } satisfies StandardColumn<FinancialTableRow>,
          ]
        : []),
      {
        id: 'company',
        label: 'Empresa',
        accessor: (row) => row.company ?? '',
        cell: (row) => <>{row.company ?? '—'}</>,
      },
      {
        id: 'businessUnit',
        label: 'BU',
        accessor: (row) => row.businessUnit ?? '',
        cell: (row) => <>{row.businessUnit ?? '—'}</>,
      },
      {
        id: 'status',
        label: 'Status',
        accessor: (row) => getStatusLabel(row.status),
        cell: (row) => <StatusBadge status={row.status} />,
        exportValue: (row) => getStatusLabel(row.status),
      },
      {
        id: 'type',
        label: 'Conta a',
        accessor: (row) => getAccountLabel(row.type),
        cell: (row) => <>{getAccountLabel(row.type)}</>,
      },
      {
        id: 'dueDate',
        label: 'Vencimento',
        accessor: (row) => row.dueDate ?? '',
        cell: (row) => <>{formatCalendarDateBR(row.dueDate)}</>,
        exportValue: (row) => formatCalendarDateBR(row.dueDate),
      },
    ];

    if (isPaymentView) {
      return [
        {
          id: 'entryNumber',
          label: 'Número do Lançamento',
          accessor: (row) => row.entryNumber ?? '',
          cell: (row) => <>{row.entryNumber ?? '—'}</>,
        },
        ...sharedColumns,
      ];
    }

    return sharedColumns;
  }, [isPaymentView, isItemView]);

  const screenKey = `financial_entries_${viewBy}`;
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(screenKey, columns);

  if (isPending) {
    return <MessageContainer>Carregando lançamentos...</MessageContainer>;
  }

  if (isError) {
    return (
      <ErrorMessage>
        Erro ao carregar lançamentos: {(error as Error)?.message ?? 'Tente novamente.'}
      </ErrorMessage>
    );
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={tableRows}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        countLabel="lançamentos"
        totalCount={total}
        hasMore={!!hasNextPage}
        isLoading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        exportFileName={`financeiro-${viewBy}`}
        emptyMessage="Nenhum lançamento encontrado."
        getSubRows={(row) => row.subRows}
        getRowId={(row) => row.key}
        expandColumnId="numero"
        renderActions={(row) =>
          row.kind === 'entry' || row.kind === 'payment' ? (
            <div onClick={(event) => event.stopPropagation()}>
              <ActionsDropdown
                entry={row.entry}
                onView={(entryId) => setViewEntryId(entryId)}
                onUpdated={handleEntryUpdated}
              />
            </div>
          ) : null
        }
      />

      {viewEntryId !== null && (
        <ViewFinancialEntryModal
          entryId={viewEntryId}
          onClose={() => setViewEntryId(null)}
          onUpdated={handleEntryUpdated}
        />
      )}
    </>
  );
};

export default FinancialEntriesTable;
